import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useUserData } from '../../../hooks/useUserData';
import { useSubscription } from '../../../contexts/SubscriptionContext';
import { useDemo } from '../../../contexts/DemoContext';

const SurplusRoutingSection = () => {
  const { userData, saveSurplusRouting, loading } = useUserData();
  const { canWrite } = useSubscription();
  const { isDemoModeActive } = useDemo();
  const effectiveCanWrite = canWrite && !isDemoModeActive;

  const portfolioTypes = useMemo(() => {
    const types = new Set((userData?.portfolio || []).map(a => a.type).filter(Boolean));
    return Array.from(types);
  }, [userData]);

  // Ensure 'Cash' is always available for routing, even if not present in portfolio
  const routingTypes = useMemo(() => {
    const set = new Set(portfolioTypes);
    set.add('Cash');
    return Array.from(set);
  }, [portfolioTypes]);

  const recommendedMix = {
    'Cash': 20,
    'Fixed Deposits': 20,
    'Debt MF': 25,
    'Eq MF': 25,
    'Equity': 10
  };

  const initialRows = useMemo(() => {
    const persisted = userData?.surplusRouting;
    if (Array.isArray(persisted) && persisted.length > 0) {
      const filtered = persisted
        .filter(r => routingTypes.includes(r.assetClass))
        .map(r => ({
          assetClass: r.assetClass,
          include: Boolean(r.include),
          weight: typeof r.weight === 'number' ? Math.max(0, Math.min(100, r.weight)) : 0
        }));
      const hasCash = filtered.some(r => r.assetClass === 'Cash');
      if (!hasCash) {
        const sumOther = filtered.filter(r => r.assetClass !== 'Cash' && r.include).reduce((s, r) => s + (Number(r.weight) || 0), 0);
        const cashWeight = Math.max(0, 100 - sumOther);
        filtered.push({ assetClass: 'Cash', include: true, weight: cashWeight });
      }
      return filtered;
    }
    // Default: include Cash with 100% if no other routing types exist; otherwise include Cash at 0%
    const hasOtherTypes = routingTypes.some(t => t !== 'Cash');
    const base = routingTypes.filter(t => t !== 'Cash').map(t => ({ assetClass: t, include: false, weight: 0 }));
    const cashRow = { assetClass: 'Cash', include: true, weight: hasOtherTypes ? 0 : 100 };
    return [...base, cashRow];
  }, [userData, routingTypes]);

  const [rows, setRows] = useState(initialRows);
  const [newClassToAdd, setNewClassToAdd] = useState('');

  useEffect(() => {
    setRows(initialRows);
  }, [initialRows]);

  // Auto-recompute Cash weight as remainder of 100 minus other included weights
  useEffect(() => {
    setRows(prev => {
      const cashIdx = prev.findIndex(r => r.assetClass === 'Cash');
      if (cashIdx === -1) return prev;
      const sumOthers = prev
        .filter((r, i) => i !== cashIdx && r.include)
        .reduce((s, r) => s + (Number(r.weight) || 0), 0);
      const remainder = Math.max(0, 100 - sumOthers);
      const cash = prev[cashIdx];
      if (cash.include !== true || Math.abs(Number(cash.weight) - remainder) > 0.0001) {
        return prev.map((r, i) => i === cashIdx ? { ...r, include: true, weight: remainder } : r);
      }
      return prev;
    });
  }, [rows]);

  const remainingTypes = useMemo(() => {
    const present = new Set(rows.map(r => r.assetClass));
    return routingTypes.filter(t => !present.has(t));
  }, [rows, routingTypes]);

  useEffect(() => {
    if (remainingTypes.length > 0) setNewClassToAdd(remainingTypes[0]);
    else setNewClassToAdd('');
  }, [remainingTypes]);

  const totalPercent = useMemo(() => rows.reduce((sum, r) => sum + (r.include ? (Number(r.weight) || 0) : 0), 0), [rows]);
  const isValid = useMemo(() => {
    // Only validate individual weights; remainder will be assigned to Cash on save
    return rows.every(r => {
      if (!r.include && r.assetClass !== 'Cash') return true;
      const w = Number(r.weight);
      return !Number.isNaN(w) && w >= 0 && w <= 100;
    });
  }, [rows]);

  const handleIncludeToggle = (index) => {
    setRows(prev => prev.map((r, i) => i === index ? { ...r, include: !r.include, weight: !r.include ? r.weight : 0 } : r));
  };

  const handleWeightChange = (index, value) => {
    if (value === '') {
      setRows(prev => prev.map((r, i) => i === index ? { ...r, weight: '', include: true } : r));
      return;
    }
    const numeric = Math.max(0, Math.min(100, Number(value)));
    if (Number.isNaN(numeric)) return;
    setRows(prev => prev.map((r, i) => i === index ? { ...r, weight: numeric, include: true } : r));
  };

  const addRow = () => {
    if (!newClassToAdd) return;
    setRows(prev => [...prev, { assetClass: newClassToAdd, include: false, weight: 0 }]);
  };

  const removeRow = (index) => setRows(prev => prev.filter((r, i) => i !== index || r.assetClass === 'Cash'));

  const applyRecommended = () => {
    setRows(prev => prev.map(r => {
      const rec = recommendedMix[r.assetClass] ?? 0;
      const include = rec > 0;
      return { ...r, include, weight: include ? rec : 0 };
    }));
  };

  const saveRouting = async () => {
    // Defer normalization to the data hook; just pass current rows
    const items = rows.map(r => ({ assetClass: r.assetClass, include: Boolean(r.include), weight: r.weight === '' ? 0 : (Number(r.weight) || 0) }));
    await saveSurplusRouting(items);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="Sliders" size={16} className="text-accent" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">
              <span title="Premium Feature - Advanced surplus allocation across asset classes" className="cursor-help">🚀</span> Surplus Routing
            </h3>
            <p className="text-sm text-muted-foreground">Allocate yearly surplus across asset classes</p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <select
              className="text-sm border border-border rounded px-2 py-1 bg-background"
              value={newClassToAdd}
              onChange={(e) => setNewClassToAdd(e.target.value)}
              disabled={remainingTypes.length === 0}
            >
              {remainingTypes.length === 0 ? (
                <option value="">All classes added</option>
              ) : remainingTypes.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <Button variant="outline" size="sm" onClick={addRow} disabled={!newClassToAdd || !effectiveCanWrite}>
              <Icon name="Plus" size={16} />
              Add Class
            </Button>
          </div>
          <Button variant="outline" size="sm" onClick={applyRecommended} disabled={!effectiveCanWrite}>
            <Icon name="Sparkles" size={16} />
            Use Recommended Mix
          </Button>
          <Button onClick={saveRouting} disabled={!isValid || loading || !effectiveCanWrite} title={isDemoModeActive ? 'Cannot save in demo mode' : ''}>
            <Icon name="Save" size={16} />
            Save Routing
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Asset Class</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Weight %</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Include</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((row, index) => (
              <tr key={`${row.assetClass}-${index}`} className="hover:bg-muted/30 transition-colors duration-200">
                <td className="px-4 py-4">
                  <span className="text-sm text-foreground">{row.assetClass}</span>
                </td>
                <td className="px-4 py-4">
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={0.1}
                    className={`w-28 text-sm border rounded px-2 py-1 bg-background ${row.include ? 'border-border' : 'border-border/50 text-muted-foreground'} ${row.assetClass === 'Cash' ? 'opacity-70 cursor-not-allowed' : ''}`}
                    value={row.weight}
                    onChange={(e) => handleWeightChange(index, e.target.value)}
                    disabled={row.assetClass === 'Cash'}
                  />
                </td>
                <td className="px-4 py-4">
                  <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={row.include}
                      onChange={() => handleIncludeToggle(index)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-foreground">{row.include ? 'Yes' : 'No'}</span>
                  </label>
                </td>
                <td className="px-4 py-4 text-right">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeRow(index)}
                    className="text-muted-foreground hover:text-destructive"
                    title="Remove row"
                    disabled={row.assetClass === 'Cash'}
                  >
                    <Icon name="Trash2" size={16} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-muted/30 border-t border-border">
              <td className="px-4 py-3 text-sm font-medium text-muted-foreground">Total % (included)</td>
              <td className="px-4 py-3" colSpan={3}>
                <div className="flex items-center gap-3">
                  <div className={`text-sm font-semibold ${Math.round(totalPercent) <= 100 ? 'text-success' : 'text-destructive'}`}>
                    {totalPercent.toFixed(1)}%
                  </div>
                  <span className="text-xs text-muted-foreground">{Math.round(totalPercent) <= 100 ? 'Remainder will be routed to Cash' : 'Please reduce weights to at most 100%'}</span>
                </div>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default SurplusRoutingSection;


